import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Crown, ChevronRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { trackEvent } from "@/lib/analytics";

/**
 * Reusable section that surfaces premium tools on a context page (pregnancy
 * explorer, TTC dashboard). Each tile is a quick link that scrolls to or
 * routes to the relevant feature on the main Dashboard, where the
 * `PremiumGate` wrapper will handle the actual lock UX for free users and
 * full functionality for premium users. No need for a duplicate gate here —
 * the tiles always link out, the destination handles the rest.
 */
const PremiumToolsSection = ({ title = "Premium tools", tools }) => {
  if (!tools?.length) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      data-testid="premium-tools-section"
    >
      <div className="flex items-center gap-2 mb-3">
        <Crown className="w-4 h-4 text-moon-gold" />
        <h3 className="text-mystic-200 text-sm uppercase tracking-widest">{title}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tools.map(({ icon: Icon, label, description, to, accent, testId }) => (
          <Link
            key={to + label}
            to={to}
            data-testid={testId}
            onClick={() => trackEvent("premium_tool_clicked", "premium_tools_section", { tool: label })}
            className="group block"
          >
            <Card className="glass border-white/10 hover:border-moon-gold/30 transition-all">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-moon-gold/15 text-moon-gold"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm leading-tight">{label}</p>
                  {description && (
                    <p className="text-mystic-300 text-xs mt-0.5 leading-snug">{description}</p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-mystic-400 group-hover:text-moon-gold transition" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
      <p className="text-mystic-400/70 text-xs mt-3 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" />
        Tools require Premium. Free users will see a preview on the dashboard.
      </p>
    </motion.section>
  );
};

export default PremiumToolsSection;
